import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Chip,
  IconButton,
  Stack,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  Person as ProfileIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as AuditIcon,
  ExitToApp as LogoutIcon,
  ArrowForward as ArrowForwardIcon,
  Launch as LaunchIcon,
  Lock as LockIcon,
  Upgrade as UpgradeIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/hooks/useAuth";
import { navigateToHRModule } from "../../../utils/hrNavigation";
import accountingLogo from "../../../assets/modules-logos/accounting.png";
import posLogo from "../../../assets/modules-logos/pos.png";
import hrLogo from "../../../assets/modules-logos/hr.png";
import stockLogo from "../../../assets/modules-logos/stock.png";
import {
  PermissionsFormat,
  getUserAdminModules,
} from "../../auth/utils/permission.utils";

interface ModuleCard {
  id: string;
  name: string;
  description: string;
  logo: string;
  gradientColors: string[];
  route: string;
  moduleKey: string; // Key that maps to the permission structure
  subtitle?: string;
}

interface ModuleWithAccess extends ModuleCard {
  hasAccess: boolean;
}

// Helper function to check if user has any permissions for a specific module
const hasModuleAccess = (
  moduleKey: string,
  permissions: PermissionsFormat | undefined | null,
  newPermissions: any | undefined | null,
  isStoreOwner: boolean = false
): boolean => {
  // Store owners have access to everything
  if (isStoreOwner) {
    return true;
  }

  // First, check the new permissions structure
  if (newPermissions && typeof newPermissions === "object") {
    const modulePermissions = newPermissions[moduleKey];
    if (modulePermissions && Array.isArray(modulePermissions)) {
      // Check if user has any permissions for this module across all locations
      const hasAnyLocationPermissions = modulePermissions.some(
        (locationData: any) => {
          return (
            locationData &&
            locationData.permissions &&
            Array.isArray(locationData.permissions) &&
            locationData.permissions.length > 0
          );
        }
      );

      if (hasAnyLocationPermissions) {
        return true;
      }
    }
  }

  // Fall back to old permissions structure if newPermissions doesn't have the module
  if (!permissions) {
    return false;
  }

  // Handle legacy permissions (array format)
  if (Array.isArray(permissions)) {
    // For legacy format, check if user has any permissions related to the module
    return permissions.some((permission) => permission.startsWith(moduleKey));
  }

  // Handle nested permissions (object format)
  const modulePermissions = permissions[moduleKey];
  if (!modulePermissions) {
    return false;
  }

  // Check if user has any permissions for this module across all branches/warehouses/departments
  return Object.keys(modulePermissions).length > 0;
};

const availableModules: ModuleCard[] = [
  {
    id: "inventory",
    name: "Stock Manager",
    subtitle: "VenFlows",
    description: "Inventory management and stock control",
    logo: stockLogo,
    gradientColors: ["#06B6D4", "#0891B2", "#22D3EE"],
    route: "/business",
    moduleKey: "inventory",
  },
  {
    id: "accounting",
    name: "Finance",
    subtitle: "VenFlows",
    description: "Financial records and transaction management",
    logo: accountingLogo,
    gradientColors: ["#7F79A2", "#5C5674", "#A9A3C8"],
    route: "/accounting",
    moduleKey: "accounting",
  },
  {
    id: "pos",
    name: "POS",
    subtitle: "VenFlows",
    description: "Point of sale and retail operations",
    logo: posLogo,
    gradientColors: ["#31B5A9", "#1E8078", "#55D7C7"],
    route: "/pos",
    moduleKey: "pos",
  },
  {
    id: "hr",
    name: "HR",
    subtitle: "VenFlows",
    description: "Human resources and staff management",
    logo: hrLogo,
    gradientColors: ["#9B2F5D", "#D45D79", "#F2A7B3"],
    route: "/hr",
    moduleKey: "hr",
  },
];

export const AppLauncherPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [userAdminModules, setUserAdminModules] = useState<string[]>([]);
  const [adminModulesLoading, setAdminModulesLoading] = useState(true);

  // Check user admin modules
  const checkUserAdminModules = async () => {
    if (!user?._id || !user?.store_id) {
      setUserAdminModules([]);
      setAdminModulesLoading(false);
      return;
    }

    try {
      setAdminModulesLoading(true);
      const adminModules = await getUserAdminModules(
        user._id,
        user.store_id,
        user.is_store_owner
      );
      setUserAdminModules(adminModules);
    } catch (error) {
      console.error("Error checking user admin modules:", error);
      setUserAdminModules([]);
    } finally {
      setAdminModulesLoading(false);
    }
  };

  const handleModuleClick = (module: ModuleWithAccess) => {
    if (!module.hasAccess) {
      // Handle disabled module clicks
      if (user?.is_store_owner) {
        // Store owner - navigate to subscription upgrade
        navigate("/subscription/upgrade");
      }
      // Regular users - do nothing (handled by tooltip)
      return;
    }

    if (module.moduleKey === "hr") {
      // Navigate to external HR module in a new tab
      navigateToHRModule({ openInNewTab: true });
      return;
    }

    navigate(module.route);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/auth/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const getModulesWithAccess = () => {
    return availableModules.map((module) => ({
      ...module,
      hasAccess: hasModuleAccess(
        module.moduleKey,
        user?.permissions,
        user?.newPermissions,
        user?.is_store_owner
      ),
    }));
  };

  const canViewAuditLogs = () => {
    // User can view audit logs if they are a store owner or have admin access to any module
    return user?.is_store_owner || userAdminModules.length > 0;
  };

  const canManagePermissions = () => {
    // User can manage permissions if they are a store owner or have admin access to any module
    return user?.is_store_owner || userAdminModules.length > 0;
  };

  // Check user admin modules when user changes
  useEffect(() => {
    checkUserAdminModules();
  }, [user?._id, user?.store_id]);

  if (!user) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, ease: "easeOut" }}
    >
      <Box
        sx={{
          minHeight: "100vh",
          background: `
            linear-gradient(135deg, #0f172a 0%, #1e293b 30%, #0f172a 100%),
            radial-gradient(circle at 20% 30%, rgba(49, 181, 169, 0.15) 0%, transparent 60%),
            radial-gradient(circle at 80% 70%, rgba(6, 182, 212, 0.12) 0%, transparent 60%)
          `,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
            zIndex: 1,
          },
        }}
      >
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
            border: "none",
            borderBottom: "2px solid #31B5A9",
            mb: 0,
            borderRadius: 0,
            position: "sticky",
            top: 0,
            zIndex: 100,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
        >
          <Container maxWidth="xl">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <Box sx={{ py: 3 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "#ffffff",
                        mb: 0.5,
                        fontSize: { xs: "1.5rem", md: "2rem" },
                        textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                        userSelect: "none",
                      }}
                    >
                      Welcome back, {user.first_name}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      {/* <Chip
                    label={user.role?.replace(/_/g, " ").toUpperCase()}
                    size="small"
                    sx={{
                      fontWeight: 500,
                      background: "rgba(49, 181, 169, 0.15)",
                      color: "#22D3EE",
                      border: "1px solid rgba(34, 211, 238, 0.2)",
                      borderRadius: 1,
                      fontSize: "0.75rem",
                    }}
                  /> */}
                      {user.assignment_details?.assignment_name && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(255, 255, 255, 0.5)",
                            fontSize: "0.875rem",
                            userSelect: "none",
                          }}
                        >
                          {user.assignment_details.assignment_name}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
                  >
                    <Stack direction="row" spacing={1}>
                      {!adminModulesLoading && canManagePermissions() && (
                        <Tooltip
                          title="User Management"
                          placement="bottom"
                          arrow
                          slotProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "#000000",
                                color: "#ffffff",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                borderRadius: 1,
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                                "& .MuiTooltip-arrow": {
                                  color: "#000000",
                                },
                              },
                            },
                          }}
                        >
                          <IconButton
                            onClick={() => navigate("/admin/permissions")}
                            size="small"
                            sx={{
                              width: 40,
                              height: 40,
                              background: "rgba(255, 255, 255, 0.05)",
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                              color: "rgba(255, 255, 255, 0.7)",
                              transition:
                                "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                              "&:hover": {
                                background: "rgba(139, 92, 246, 0.15)",
                                borderColor: "rgba(139, 92, 246, 0.4)",
                                color: "#8b5cf6",
                                transform: "translateY(-1px)",
                                boxShadow:
                                  "0 4px 12px rgba(139, 92, 246, 0.25)",
                              },
                            }}
                          >
                            <AdminIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!adminModulesLoading && canViewAuditLogs() && (
                        <Tooltip
                          title="Audit Logs"
                          placement="bottom"
                          arrow
                          slotProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "#000000",
                                color: "#ffffff",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                borderRadius: 1,
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                                "& .MuiTooltip-arrow": {
                                  color: "#000000",
                                },
                              },
                            },
                          }}
                        >
                          <IconButton
                            onClick={() => navigate("/admin/audit-logs")}
                            size="small"
                            sx={{
                              width: 40,
                              height: 40,
                              background: "rgba(255, 255, 255, 0.05)",
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                              color: "rgba(255, 255, 255, 0.7)",
                              transition:
                                "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                              "&:hover": {
                                background: "rgba(245, 158, 11, 0.15)",
                                borderColor: "rgba(245, 158, 11, 0.4)",
                                color: "#f59e0b",
                                transform: "translateY(-1px)",
                                boxShadow:
                                  "0 4px 12px rgba(245, 158, 11, 0.25)",
                              },
                            }}
                          >
                            <AuditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip
                        title="View Profile"
                        placement="bottom"
                        arrow
                        slotProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "#000000",
                              color: "#ffffff",
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              borderRadius: 1,
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                              "& .MuiTooltip-arrow": {
                                color: "#000000",
                              },
                            },
                          },
                        }}
                      >
                        <IconButton
                          onClick={() => navigate("/profile")}
                          size="small"
                          sx={{
                            width: 40,
                            height: 40,
                            background: "rgba(255, 255, 255, 0.05)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            color: "rgba(255, 255, 255, 0.7)",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              background: "rgba(49, 181, 169, 0.15)",
                              borderColor: "rgba(49, 181, 169, 0.4)",
                              color: "#31B5A9",
                              transform: "translateY(-1px)",
                              boxShadow: "0 4px 12px rgba(49, 181, 169, 0.25)",
                            },
                          }}
                        >
                          <ProfileIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title="Logout"
                        placement="bottom"
                        arrow
                        slotProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "#000000",
                              color: "#ffffff",
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              borderRadius: 1,
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                              "& .MuiTooltip-arrow": {
                                color: "#000000",
                              },
                            },
                          },
                        }}
                      >
                        <IconButton
                          onClick={handleLogout}
                          size="small"
                          sx={{
                            width: 40,
                            height: 40,
                            background: "rgba(239, 68, 68, 0.1)",
                            border: "1px solid rgba(239, 68, 68, 0.3)",
                            color: "#ef4444",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              background: "rgba(239, 68, 68, 0.2)",
                              borderColor: "rgba(239, 68, 68, 0.6)",
                              color: "#ffffff",
                              transform: "translateY(-1px)",
                              boxShadow: "0 4px 12px rgba(239, 68, 68, 0.4)",
                            },
                          }}
                        >
                          <LogoutIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </motion.div>
                </Stack>
              </Box>
            </motion.div>
          </Container>
        </Paper>

        <Container
          maxWidth="xl"
          sx={{ py: 8, position: "relative", zIndex: 2 }}
        >
          {/* Module Selector Area */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  textAlign: "center",
                  mb: 8,
                  fontSize: "1.2rem",
                  fontWeight: 500,
                  textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                  userSelect: "none",
                }}
              >
                Select a module to access your business tools
              </Typography>
            </motion.div>

            <Box
              sx={{
                position: "relative",
                maxWidth: 900,
                mx: "auto",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gridTemplateRows: "1fr 1fr",
                gap: 6,
                alignItems: "center",
                minHeight: 400,
              }}
            >
              {getModulesWithAccess().map(
                (module: ModuleWithAccess, index: number) => (
                  <Box
                    key={module.id}
                    sx={{
                      gridColumn: (index % 2) + 1,
                      gridRow: Math.floor(index / 2) + 1,
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 50, scale: 0.85 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{
                        duration: 0.7,
                        delay: index * 0.15,
                        ease: [0.34, 1.56, 0.64, 1],
                        type: "spring",
                        stiffness: 100,
                        damping: 15,
                      }}
                      whileHover={{
                        y: -6,
                        transition: { duration: 0.2, ease: "easeOut" },
                      }}
                      whileTap={{
                        scale: 0.98,
                        y: -3,
                        transition: { duration: 0.1, ease: "easeOut" },
                      }}
                    >
                      <Tooltip
                        title={
                          !module.hasAccess ? (
                            <Box sx={{ p: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  mb: 1,
                                  color: "#ffffff",
                                }}
                              >
                                {user?.is_store_owner
                                  ? "Module Not Available"
                                  : "Access Denied"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "rgba(255, 255, 255, 0.9)",
                                  mb: user?.is_store_owner ? 1 : 0,
                                }}
                              >
                                {user?.is_store_owner
                                  ? "This module requires a subscription upgrade to access."
                                  : "You don't have permission to access this module. Please contact your administrator."}
                              </Typography>
                              {user?.is_store_owner && (
                                <Button
                                  size="small"
                                  startIcon={<UpgradeIcon />}
                                  sx={{
                                    mt: 1,
                                    bgcolor: "primary.main",
                                    color: "white",
                                    "&:hover": { bgcolor: "primary.dark" },
                                  }}
                                >
                                  Upgrade Subscription
                                </Button>
                              )}
                            </Box>
                          ) : (
                            ""
                          )
                        }
                        arrow
                        placement="top"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "rgba(0, 0, 0, 0.9)",
                              backdropFilter: "blur(8px)",
                              borderRadius: 2,
                              maxWidth: 280,
                              "& .MuiTooltip-arrow": {
                                color: "rgba(0, 0, 0, 0.9)",
                              },
                            },
                          },
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            width: "100%",
                            height: 240,
                            background: module.hasAccess
                              ? `linear-gradient(135deg, ${module.gradientColors[0]}25 0%, ${module.gradientColors[1]}20 50%, ${module.gradientColors[2]}25 100%)`
                              : `linear-gradient(135deg, rgba(100, 100, 100, 0.15) 0%, rgba(120, 120, 120, 0.1) 50%, rgba(100, 100, 100, 0.15) 100%)`,
                            border: module.hasAccess
                              ? `2px solid ${module.gradientColors[0]}60`
                              : "2px solid rgba(120, 120, 120, 0.3)",
                            borderRadius: "8px",
                            cursor: module.hasAccess
                              ? "pointer"
                              : "not-allowed",
                            position: "relative",
                            overflow: "hidden",
                            opacity: module.hasAccess ? 1 : 0.4,
                            boxShadow: module.hasAccess
                              ? `0 8px 25px ${module.gradientColors[0]}30`
                              : "0 8px 25px rgba(0, 0, 0, 0.1)",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": module.hasAccess
                              ? {
                                  boxShadow: `
                                0 20px 35px ${module.gradientColors[0]}40,
                                0 0 0 2px ${module.gradientColors[0]}50
                              `,
                                  background: `linear-gradient(135deg, ${module.gradientColors[0]}35 0%, ${module.gradientColors[1]}30 50%, ${module.gradientColors[2]}35 100%)`,
                                  border: `2px solid ${module.gradientColors[0]}70`,
                                  "& .module-logo": {
                                    transform: "scale(1.08) translateY(-2px)",
                                  },
                                  "& .module-title": {
                                    color: "#ffffff",
                                  },
                                  "& .module-description": {
                                    color: "rgba(255, 255, 255, 0.95)",
                                  },
                                }
                              : {
                                  opacity: 0.6,
                                  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
                                },
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: "4px",
                              background: module.hasAccess
                                ? `linear-gradient(90deg, ${module.gradientColors[0]} 0%, ${module.gradientColors[1]} 50%, ${module.gradientColors[2]} 100%)`
                                : "linear-gradient(90deg, rgba(120, 120, 120, 0.5) 0%, rgba(100, 100, 100, 0.5) 50%, rgba(120, 120, 120, 0.5) 100%)",
                            },
                            "&::after": !module.hasAccess
                              ? {
                                  content: '""',
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  width: 40,
                                  height: 40,
                                  background: "rgba(0, 0, 0, 0.3)",
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  zIndex: 1,
                                }
                              : {},
                          }}
                          onClick={() => handleModuleClick(module)}
                        >
                          {!module.hasAccess && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                height: 40,
                                background: "rgba(0, 0, 0, 0.4)",
                                borderRadius: "50%",
                                backdropFilter: "blur(4px)",
                              }}
                            >
                              <LockIcon
                                sx={{
                                  color: "rgba(255, 255, 255, 0.8)",
                                  fontSize: 20,
                                }}
                              />
                            </Box>
                          )}
                          <Box
                            sx={{
                              p: 5,
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 3,
                              }}
                            >
                              <Box
                                component="img"
                                src={module.logo}
                                alt={module.name}
                                className="module-logo"
                                sx={{
                                  width: "auto",
                                  height: 50,
                                  maxHeight: 50,
                                  objectFit: "contain",
                                  filter:
                                    "brightness(1.2) contrast(1.1) drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                                  transition:
                                    "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                              />
                            </Box>

                            <motion.div
                              initial={{ opacity: 0, y: 15 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                duration: 0.4,
                                delay: index * 0.15 + 0.6,
                                ease: "easeOut",
                              }}
                            >
                              <Typography
                                variant="body1"
                                className="module-description"
                                sx={{
                                  color: "rgba(255, 255, 255, 0.9)",
                                  textAlign: "center",
                                  fontSize: "1rem",
                                  lineHeight: 1.5,
                                  fontWeight: 500,
                                  textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                                  transition: "all 0.3s ease",
                                  userSelect: "none",
                                }}
                              >
                                {module.description}
                              </Typography>
                            </motion.div>
                          </Box>
                        </Paper>
                      </Tooltip>
                    </motion.div>
                  </Box>
                )
              )}
            </Box>
          </motion.div>
        </Container>
      </Box>
    </motion.div>
  );
};
